import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, GripVertical, Check, Trophy, XCircle, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESET_COLORS = ['#94a3b8', '#3b82f6', '#d4af37', '#10b981', '#ef4444', '#a855f7', '#f97316', '#ec4899'];

/**
 * PipelineStageManager — modal for adding, renaming, reordering, and
 * deleting the user's custom CRM pipeline stages.
 *
 * Uses HTML5 drag/drop for reordering. Deleted stages migrate their
 * contacts to the first non-archived stage server-side (see
 * community.py:replace_pipeline_stages).
 *
 * Props:
 *   open: bool
 *   initialStages: [{id, label, color, is_archived, is_won, is_lost, order}]
 *   onClose(), onSaved(newStages) — called after a successful PUT.
 */
export default function PipelineStageManager({ open, initialStages, onClose, onSaved, getAuthHeader }) {
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    if (open) {
      setStages(JSON.parse(JSON.stringify(initialStages || [])));
    }
  }, [open, initialStages]);

  if (!open) return null;

  const updateStage = (idx, patch) => {
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addStage = () => {
    const nextIdx = stages.length;
    setStages([...stages, {
      id: '',                  // server will slugify the label
      label: 'New Stage',
      color: PRESET_COLORS[nextIdx % PRESET_COLORS.length],
      is_archived: false,
      is_won: false,
      is_lost: false,
      order: nextIdx,
    }]);
  };

  const removeStage = (idx) => {
    if (stages.length === 1) {
      toast.error('At least one stage is required');
      return;
    }
    setStages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setStages((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(dragIdx, 1);
      copy.splice(idx, 0, moved);
      return copy;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const save = async () => {
    if (stages.some((s) => !s.label.trim())) {
      toast.error('Every stage needs a label');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_URL}/api/crm/pipeline/stages`,
        { stages: stages.map((s) => ({
          id: s.id || undefined,
          label: s.label,
          color: s.color,
          is_archived: !!s.is_archived,
          is_won: !!s.is_won,
          is_lost: !!s.is_lost,
        })) },
        { headers: getAuthHeader() },
      );
      const migrated = (res.data?.removed_stages || []).length;
      toast.success(migrated ? `Saved (${migrated} stage${migrated > 1 ? 's' : ''} migrated)` : 'Pipeline saved');
      onSaved(res.data.stages);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save pipeline');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="pipeline-manager-modal">
      <div className="bg-brand-surface border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-white/5 flex items-center justify-between">
          <div>
            <h2 className="font-playfair text-xl text-white">Customize Pipeline</h2>
            <p className="text-xs text-gray-400 mt-0.5">Drag rows to reorder. Deleting a stage moves its contacts to the first active stage.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-2">
          {stages.map((s, idx) => (
            <div
              key={idx}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/5 ${dragIdx === idx ? 'opacity-50' : ''}`}
              data-testid={`stage-row-${idx}`}
            >
              <GripVertical className="w-4 h-4 text-gray-500 cursor-grab active:cursor-grabbing" />
              <input
                type="color"
                value={s.color}
                onChange={(e) => updateStage(idx, { color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer bg-transparent border-0"
                data-testid={`stage-color-${idx}`}
              />
              <Input
                value={s.label}
                onChange={(e) => updateStage(idx, { label: e.target.value })}
                placeholder="Stage name"
                className="flex-1 bg-white/5 border-white/10 text-white text-sm h-9"
                data-testid={`stage-label-${idx}`}
              />
              <button
                onClick={() => updateStage(idx, { is_won: !s.is_won, is_lost: s.is_won ? s.is_lost : false })}
                className={`p-2 rounded-md border ${s.is_won ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : 'border-white/10 text-gray-500 hover:text-white'}`}
                title="Mark as Won stage"
                data-testid={`stage-won-${idx}`}
              >
                <Trophy className="w-4 h-4" />
              </button>
              <button
                onClick={() => updateStage(idx, { is_lost: !s.is_lost, is_won: s.is_lost ? s.is_won : false })}
                className={`p-2 rounded-md border ${s.is_lost ? 'bg-rose-500/20 border-rose-500/40 text-rose-300' : 'border-white/10 text-gray-500 hover:text-white'}`}
                title="Mark as Lost stage"
                data-testid={`stage-lost-${idx}`}
              >
                <XCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => updateStage(idx, { is_archived: !s.is_archived })}
                className={`p-2 rounded-md border ${s.is_archived ? 'bg-brand-gold/20 border-brand-gold/40 text-brand-gold' : 'border-white/10 text-gray-500 hover:text-white'}`}
                title="Archive stage (hidden from main board)"
                data-testid={`stage-archived-${idx}`}
              >
                <Archive className="w-4 h-4" />
              </button>
              <button
                onClick={() => removeStage(idx)}
                className="p-2 rounded-md border border-white/10 text-gray-500 hover:text-rose-400"
                title="Delete stage"
                data-testid={`stage-delete-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button
            variant="outline"
            onClick={addStage}
            className="w-full border-dashed border-white/20 text-gray-400 hover:text-white hover:border-brand-gold/60 mt-2"
            data-testid="stage-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Stage
          </Button>
        </div>

        <div className="p-6 border-t border-white/5 flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/10 text-white hover:bg-white/5">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="flex-1 bg-brand-gold text-black hover:brightness-110 font-semibold"
            data-testid="stage-save-btn"
          >
            {saving ? 'Saving…' : <><Check className="w-4 h-4 mr-1" /> Save Pipeline</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
